import { useState, useRef, useEffect } from 'react';
import { IoChevronDownOutline } from 'react-icons/io5';

interface Option {
  value: string;
  label: string;
}

interface FilterDropdownProps {
  options: Option[];
  defaultValue?: string;
  defaultLabel?: string;
  icon?: React.ReactNode;
  onChange?: (value: string) => void;
}

const widgetFont = { fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' };

export const FilterDropdown = ({ options, defaultValue = '', defaultLabel = '', icon, onChange }: FilterDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Find the current option based on defaultValue
  const currentOption = options.find(opt => opt.value === defaultValue);
  const displayLabel = currentOption ? currentOption.label : defaultLabel;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option: Option) => {
    setIsOpen(false);
    onChange?.(option.value);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`h-9 rounded-lg bg-white border border-[#eaeaea] text-[#444] px-3 py-1.5 cursor-pointer transition-all duration-150 hover:border-[#ddd] hover:bg-[#fafafa] flex items-center gap-2 text-[13px] min-w-0 ${isOpen ? 'border-[#ccc] bg-[#fafafa]' : ''}`}
        style={widgetFont}
      >
        <div className="flex items-center gap-2 flex-1">
          {icon && <span className="text-[#888] text-[12px]">{icon}</span>}
          <span className="text-[#444] truncate font-medium">{displayLabel}</span>
        </div>
        <IoChevronDownOutline className={`text-[#888] w-3 h-3 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-1.5 min-w-full w-max bg-white rounded-xl border border-[#eaeaea] shadow-[0_4px_12px_rgba(0,0,0,0.08)] py-1 z-50" style={widgetFont}>
          {options.map((option) => (
            <button
              key={option.value}
              onClick={() => handleSelect(option)}
              className={`w-full px-3 py-1.5 text-left text-[13px] transition-colors duration-100 flex items-center gap-2 ${
                option.value === defaultValue
                  ? 'text-[#171717] font-medium bg-[#fafafa]'
                  : 'text-[#666] hover:text-[#171717] hover:bg-[#fafafa]'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
